// ATLAS-5793: editor plugin that applies the editor's content <style> blocks, scoped to this editor's editable.
//
// It keeps ONE owned <style> element in document.head, written with textContent only, and removes it on clear/destroy. The
// CSS comes from ckeditor-content-style.ts (CSSOM-parsed, every selector confined to this editor's scope token).
//
// SYNC: This file exists in two byte-identical copies:
//   Frontend/Atlas.Spa.Backoffice/src/app/shared/widgets/ckeditor-content-style-plugin.ts
//   Frontend/Atlas.Spa.Portal/src/app/shared/widgets/ckeditor-content-style-plugin.ts
// Keep them in sync; any change goes into both (operator decision 2026-10-13: no shared front-end module).
//
// No runtime imports: shaped like createAlignedDivTableGuard, a `function` plugin with plain on/off listeners.
import type { PluginConstructor } from 'https://cdn.ckeditor.com/typings/ckeditor5.d.ts';
import {
  CONTENT_STYLE_OWNER_ATTRIBUTE,
  CONTENT_STYLE_SCOPE_ATTRIBUTE,
  buildScopedContentCss,
  collectModelStyleBlocks,
  prepareHtmlForParse,
} from './ckeditor-content-style';

export const CONTENT_STYLE_PLUGIN_NAME = 'ContentStyleScope';
const PREPARED_MARKER = '__wlContentStylePrepared';

// R4: the Font plugin renders an inline color/background/size/family as its own <span> at priority 7, and GHS renders the
// template's own span (htmlSpan: classes, remaining styles, attributes) at the default 10. Lower priority renders outer,
// so the class span ended up inside the font span and its class rule (now applied, R1) beat the inline value. Rendering
// htmlSpan at 6 keeps it outside the font spans and inside links (5), so the author's inline value wins as written.
export const HTML_SPAN_VIEW_PRIORITY = 6;

/** Downcast for GHS `htmlSpan`, as GHS renders it (attributes, styles, classes) but at HTML_SPAN_VIEW_PRIORITY. */
export function htmlSpanToView(value: any, { writer }: { writer: any }): any {
  if (!value) {
    return null;
  }
  const element = writer.createAttributeElement('span', null, { priority: HTML_SPAN_VIEW_PRIORITY });
  for (const [key, attributeValue] of Object.entries(value.attributes ?? {})) {
    writer.setAttribute(key, attributeValue, element);
  }
  if (value.styles) {
    writer.setStyle(value.styles, element);
  }
  if (value.classes) {
    writer.addClass(value.classes, element);
  }
  return element;
}
/** cloud.CKEditor; nothing is needed from it today (kept for shape parity with the guard). */
export type ContentStylePluginRuntime = object;

function newScopeToken(): string {
  const c: Crypto | undefined = (globalThis as any).crypto;
  if (c?.randomUUID) {
    return 'wl-cs-' + c.randomUUID();
  }
  const bytes = new Uint8Array(16);
  c?.getRandomValues?.(bytes);
  return 'wl-cs-' + Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function createContentStylePlugin(_ck: ContentStylePluginRuntime, doc: Document = document): PluginConstructor {
  function ContentStyleScope(this: any, editor: any) {
    this.editor = editor;
    this._token = newScopeToken();
    this._memo = null;
  }
  ContentStyleScope.pluginName = CONTENT_STYLE_PLUGIN_NAME;

  ContentStyleScope.prototype._findOwned = function (this: any): HTMLStyleElement | null {
    const styles = Array.from(doc.head.querySelectorAll('style')) as HTMLStyleElement[];
    return styles.find((el) => el.getAttribute(CONTENT_STYLE_OWNER_ATTRIBUTE) === this._token) ?? null;
  };

  // the scope attribute lives in the editing view only and never reaches getData()
  ContentStyleScope.prototype._assertScopeAttribute = function (this: any): void {
    const view = this.editor.editing.view;
    const root = view.document.getRoot();
    if (root && root.getAttribute(CONTENT_STYLE_SCOPE_ATTRIBUTE) !== this._token) {
      view.change((writer: any) => writer.setAttribute(CONTENT_STYLE_SCOPE_ATTRIBUTE, this._token, root));
    }
  };

  ContentStyleScope.prototype.refresh = function (this: any): void {
    try {
      this._assertScopeAttribute();
      const blocks = collectModelStyleBlocks(this.editor.model);
      const key = blocks.join('\u0000');
      if (key === this._memo) {
        return;
      }
      const css = buildScopedContentCss(blocks, this._token);
      if (!css) {
        this.clear();
        this._memo = key;
        return;
      }
      let owned = this._findOwned();
      if (!owned) {
        owned = doc.createElement('style');
        owned.setAttribute(CONTENT_STYLE_OWNER_ATTRIBUTE, this._token);
        doc.head.appendChild(owned);
      }
      owned.textContent = css;
      this._memo = key;
    } catch {
      this.clear(); // fail closed
    }
  };

  ContentStyleScope.prototype.clear = function (this: any): void {
    this._findOwned()?.remove();
    this._memo = null;
  };

  // R3: string input to the data processor (setData, leaving source editing, clipboard paste) keeps its <style> blocks
  ContentStyleScope.prototype._wrapProcessor = function (this: any, processor: any): void {
    if (!processor || typeof processor.toView !== 'function' || processor[PREPARED_MARKER]) {
      return;
    }
    const toView = processor.toView;
    processor.toView = function (this: any, data: unknown) {
      return toView.call(this, typeof data === 'string' ? prepareHtmlForParse(data) : data);
    };
    processor[PREPARED_MARKER] = true;
  };

  ContentStyleScope.prototype.init = function (this: any) {
    const editor = this.editor;
    this._wrapProcessor(editor.data?.processor);
    this._wrapProcessor(editor.data?.htmlProcessor);
    // 'high' wins over GHS's own htmlSpan converter whatever the plugin order
    editor.conversion
      ?.for('downcast')
      .attributeToElement({ model: 'htmlSpan', view: htmlSpanToView, converterPriority: 'high' });
    this._onChangeData = () => this.refresh();
    this._onReady = () => this.refresh();
    editor.model.document.on('change:data', this._onChangeData);
    editor.on('ready', this._onReady);
  };

  ContentStyleScope.prototype.destroy = function (this: any) {
    try {
      this.editor.model.document.off('change:data', this._onChangeData);
      this.editor.off('ready', this._onReady);
    } catch {}
    this.clear();
  };
  return ContentStyleScope as unknown as PluginConstructor;
}

/** Calls the plugin's clear() if the editor has it; never throws. Used by the Backoffice pool on release. */
export function clearContentStyles(editor: any): void {
  try {
    if (editor?.plugins?.has?.(CONTENT_STYLE_PLUGIN_NAME)) {
      editor.plugins.get(CONTENT_STYLE_PLUGIN_NAME)?.clear?.();
    }
  } catch {}
}
